import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplyIcon from '@mui/icons-material/Reply';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { apiFetch } from '../../api/client';
import { canPreview, fileKind, formatBytes, formatFullDate } from './emailUtils';

const ICONS = {
  pdf: PictureAsPdfIcon,
  image: ImageIcon,
  sheet: TableChartIcon,
  doc: DescriptionIcon,
  file: AttachFileIcon,
};

function Recipients({ detail }) {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" noWrap={!open}>
        <strong style={{ color: '#0f172a' }}>De</strong> {detail.from || '—'}
      </Typography>
      {open && (
        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            <strong style={{ color: '#0f172a' }}>Para</strong> {detail.to || '—'}
          </Typography>
          {detail.cc ? (
            <Typography variant="body2" color="text.secondary">
              <strong style={{ color: '#0f172a' }}>Cc</strong> {detail.cc}
            </Typography>
          ) : null}
          {detail.bcc ? (
            <Typography variant="body2" color="text.secondary">
              <strong style={{ color: '#0f172a' }}>Cco</strong> {detail.bcc}
            </Typography>
          ) : null}
        </Stack>
      )}
      <Button size="small" onClick={() => setOpen((v) => !v)} sx={{ textTransform: 'none', px: 0 }}>
        {open ? 'Ocultar destinatários' : 'Para / Cc / Cco'}
      </Button>
    </Box>
  );
}

export default function EmailReadingPane({ detail, loading, onReply, onTrash, onBack, showBack }) {
  const [preview, setPreview] = useState(null);
  const [busyId, setBusyId] = useState('');

  const openAttachment = async (att, asPreview) => {
    if (!detail?.id) return;
    setBusyId(att.id);
    try {
      const q = new URLSearchParams({
        filename: att.filename || 'anexo',
        mime: att.mimeType || 'application/octet-stream',
        preview: asPreview ? '1' : '0',
      });
      const res = await apiFetch(
        `/api/gmail/messages/${encodeURIComponent(detail.id)}/attachments/${encodeURIComponent(att.id)}?${q}`
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Não foi possível abrir o anexo.');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (asPreview) {
        setPreview({ url, kind: fileKind(att.mimeType, att.filename), name: att.filename });
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = att.filename || 'anexo';
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
      }
    } catch (e) {
      window.alert(e.message || 'Falha no anexo');
    } finally {
      setBusyId('');
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  if (loading && !detail) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box sx={{ p: 4, color: 'text.secondary' }}>
        <Typography>Selecione um e-mail para ler.</Typography>
      </Box>
    );
  }

  const when = formatFullDate(detail.date, detail.internalDate);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: '#fff',
          px: 2,
          pt: 1.5,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          {showBack && (
            <IconButton onClick={onBack} aria-label="Voltar à lista" size="small">
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.3 }}>
              {detail.subject || '(sem assunto)'}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
              <Tooltip title={when}>
                <Typography variant="caption" color="text.secondary">
                  {when}
                </Typography>
              </Tooltip>
            </Stack>
            <Box sx={{ mt: 1 }}>
              <Recipients detail={detail} />
            </Box>
          </Box>
          <Stack direction="row" spacing={0.5}>
            <Button size="small" startIcon={<ReplyIcon />} onClick={onReply}>
              Responder
            </Button>
            <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={onTrash}>
              Excluir
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
        {detail.html ? (
          <Box
            sx={{
              '& img': { maxWidth: '100%' },
              '& a': { color: 'primary.main' },
              color: 'text.primary',
              lineHeight: 1.55,
              wordBreak: 'break-word',
            }}
            dangerouslySetInnerHTML={{ __html: detail.html }}
          />
        ) : (
          <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0 }}>
            {detail.text || detail.snippet || ''}
          </Typography>
        )}

        {Array.isArray(detail.attachments) && detail.attachments.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
              Anexos
            </Typography>
            <Stack spacing={1}>
              {detail.attachments.map((att) => {
                const kind = fileKind(att.mimeType, att.filename);
                const Icon = ICONS[kind] || AttachFileIcon;
                const previewable = canPreview(kind);
                return (
                  <Stack
                    key={att.id}
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'rgba(25,118,210,0.03)',
                    }}
                  >
                    <Icon color={kind === 'pdf' ? 'error' : 'primary'} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap title={att.filename}>
                        {att.filename}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatBytes(att.size)}
                      </Typography>
                    </Box>
                    {previewable && (
                      <Button size="small" disabled={busyId === att.id} onClick={() => openAttachment(att, true)}>
                        Ver
                      </Button>
                    )}
                    <Button size="small" disabled={busyId === att.id} onClick={() => openAttachment(att, false)}>
                      Baixar
                    </Button>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        )}
      </Box>

      <Dialog open={Boolean(preview)} onClose={closePreview} maxWidth="md" fullWidth>
        <Box sx={{ p: 2 }}>
          <Typography fontWeight={700} sx={{ mb: 1 }}>
            {preview?.name}
          </Typography>
          {preview?.kind === 'image' ? (
            <Box component="img" src={preview.url} alt={preview.name} sx={{ maxWidth: '100%' }} />
          ) : preview ? (
            <Box component="iframe" title={preview.name} src={preview.url} sx={{ width: '100%', height: '70vh', border: 0 }} />
          ) : null}
          <Button sx={{ mt: 1 }} onClick={closePreview}>
            Fechar
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}
